/**
 * Utility functions for image processing before upload to Supabase
 */

/**
 * Compresses and optimizes an image before upload
 * - Reduces resolution to a maximum size
 * - Compresses quality to reduce file size
 * - Converts to WebP if supported
 * 
 * @param file Original file to be uploaded
 * @param options Configuration options
 * @returns Promise resolving to optimized file ready for upload
 */
export const optimizeImageForUpload = async (
  file: File,
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    convertToWebP?: boolean;
    maxSizeKB?: number;
  } = {}
): Promise<File> => {
  // Default options
  const maxWidth = options.maxWidth || 1920;
  const maxHeight = options.maxHeight || 1920;
  const quality = options.quality || 0.85;
  const convertToWebP = options.convertToWebP !== false; // Default to true
  const maxSizeKB = options.maxSizeKB || 500;

  // Skip optimization for non-image files or SVGs
  const fileType = file.type.toLowerCase();
  const isImage = fileType.startsWith('image/') && !fileType.includes('svg');
  if (!isImage) {
    console.log('File is not an image or is SVG, skipping optimization');
    return file;
  }

  // Skip optimization for small files unless forced to convert format
  if (file.size < maxSizeKB * 1024 && !convertToWebP) {
    console.log('File is already small enough, skipping optimization');
    return file;
  }

  try {
    // Create an image element to load the file
    const img = document.createElement('img');
    const imgLoaded = new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
    });

    // Load the image from the file
    img.src = URL.createObjectURL(file);
    await imgLoaded;

    // Calculate new dimensions maintaining aspect ratio
    let width = img.width;
    let height = img.height;

    if (width > maxWidth || height > maxHeight) {
      const aspectRatio = width / height;
      if (width > maxWidth) {
        width = maxWidth;
        height = Math.round(width / aspectRatio);
      }
      if (height > maxHeight) {
        height = maxHeight;
        width = Math.round(height * aspectRatio);
      }
    }

    // Create a canvas to draw the resized image
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Draw the image on the canvas with smooth scaling
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    // Clean up the object URL
    URL.revokeObjectURL(img.src);

    // Determine output format
    const supportsWebP = canvas.toDataURL('image/webp').startsWith('data:image/webp');
    const outputType = (convertToWebP && supportsWebP) ? 'image/webp' : file.type;
    
    // Convert the canvas to a blob with the desired format and quality
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result);
          else reject(new Error('Failed to convert canvas to blob'));
        },
        outputType,
        quality
      );
    });

    // Generate appropriate file extension
    let fileName = file.name;
    if (convertToWebP && supportsWebP) {
      // Replace existing extension with .webp
      fileName = fileName.replace(/\.[^.]+$/, '.webp');
    }

    // Create a new File object with the optimized data
    const optimizedFile = new File([blob], fileName, {
      type: outputType,
      lastModified: Date.now()
    });

    console.log(`Image optimized: ${(file.size / 1024).toFixed(1)}KB → ${(optimizedFile.size / 1024).toFixed(1)}KB (${Math.round((1 - optimizedFile.size / file.size) * 100)}% reduction)`);
    
    return optimizedFile;
  } catch (error) {
    console.error('Image optimization failed:', error);
    return file; // Return the original file if optimization fails
  }
};

/**
 * Processes multiple images for upload
 * @param files Array of files to optimize
 * @param options Optimization options
 * @returns Promise resolving to array of optimized files
 */
export const batchOptimizeImages = async (
  files: File[],
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    convertToWebP?: boolean;
    maxSizeKB?: number;
  } = {}
): Promise<File[]> => {
  const optimizationPromises = files.map(file => optimizeImageForUpload(file, options));
  return Promise.all(optimizationPromises);
}; 